import { PROGRESS, STAGE_COLORS } from "@/lib/api";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";

export const StatusPill = ({
  progress,
  onClick,
  testId,
  compact = false,
  reasons = [],
  nextStep = "",
  how = [],
  toOnTrack = [],
  days,
}) => {
  const meta = PROGRESS[progress] || PROGRESS.needs_attention;
  const Tag = onClick ? "button" : "span";
  const pill = (
    <Tag
      type={onClick ? "button" : undefined}
      data-testid={testId}
      onClick={onClick}
      className={`inline-flex items-center gap-2 border font-medium transition-colors duration-200 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary ${
        onClick ? "hover:bg-muted" : ""
      } ${compact ? "px-2 py-0.5 text-[11px]" : "px-2.5 py-1 text-xs"}`}
      style={{ borderColor: meta.color, color: meta.color }}
    >
      <span className="inline-block h-2 w-2 shrink-0" style={{ backgroundColor: meta.color }} />
      {meta.label}
    </Tag>
  );

  if (!reasons.length) return pill;

  return (
    <TooltipProvider delayDuration={120}>
      <Tooltip>
        <TooltipTrigger asChild>{pill}</TooltipTrigger>
        <TooltipContent
          side="top"
          align="start"
          data-testid={testId ? `${testId}-why` : "progress-why"}
          className="max-w-sm rounded-none border border-border bg-popover p-3 text-popover-foreground"
        >
          <div className="label-caps" style={{ color: meta.color }}>
            Why it reads {meta.label}
          </div>
          <ul className="mt-1 space-y-1">
            {reasons.map((r) => (
              <li key={r} className="text-[11px] leading-snug">
                · {r}
              </li>
            ))}
          </ul>
          {days !== null && days !== undefined && (
            <p className="mt-1 font-data text-[10px] text-muted-foreground">
              Last updated {days} day(s) ago
            </p>
          )}
          {toOnTrack.length > 0 && (
            <div className="mt-2 border-t border-border pt-1.5" data-testid="progress-to-on-track">
              <div className="label-caps" style={{ color: "hsl(var(--healthy))" }}>
                To read On Track
              </div>
              <ul className="mt-1 space-y-1">
                {toOnTrack.map((a) => (
                  <li key={a} className="text-[11px] leading-snug">
                    · {a}
                  </li>
                ))}
              </ul>
            </div>
          )}
          {how.length > 0 && (
            <div className="mt-2 border-t border-border pt-1.5" data-testid="progress-how">
              <div className="label-caps">How this is worked out</div>
              <ul className="mt-1 space-y-1">
                {how.map((h) => (
                  <li key={h} className="text-[10px] leading-snug text-muted-foreground">
                    · {h}
                  </li>
                ))}
              </ul>
            </div>
          )}
          {nextStep && (
            <p className="mt-2 border-t border-border pt-1.5 text-[11px] text-muted-foreground">
              What should happen next: {nextStep}
            </p>
          )}
        </TooltipContent>
      </Tooltip>
    </TooltipProvider>
  );
};

export const StageTag = ({ stage }) => (
  <span
    className="inline-block border px-2 py-0.5 text-[11px] font-medium"
    style={{ borderColor: STAGE_COLORS[stage] || "hsl(var(--border))" }}
  >
    {stage}
  </span>
);

export const ProgressBar = ({ value, color, height = "h-2" }) => (
  <div className={`w-full bg-muted ${height}`}>
    <div
      className={`${height} transition-[width] duration-500`}
      style={{ width: `${Math.min(100, Math.max(0, value))}%`, backgroundColor: color }}
    />
  </div>
);
